import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Slider } from './ui/slider';
import { AlertTriangle, ChevronRight, TrendingUp, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Renten-Check (§34d-konform)
 * Berechnet die Rentenlücke basierend auf:
 * - Gewünschtes Rentenniveau
 * - Gesetzliche Rente
 * - Bestehende private Altersvorsorge
 * 
 * KEINE Kapitalmarkt-Prognosen oder Rendite-Simulationen!
 * Nur die monatliche Einkommenslücke im Rentenalter.
 */

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Info-Tooltip Komponente
const InfoTooltip = ({ text }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="text-slate/50 hover:text-slate transition-colors focus:outline-none"
        aria-label="Info"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {isOpen && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 sm:w-72">
          <div className="bg-navy text-white text-xs leading-relaxed rounded-lg px-3 py-2.5 shadow-xl">
            {text}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-navy rotate-45" />
        </div>
      )}
    </span>
  );
};

export const RentenCheck = ({ showHeader = true, headerConfig = null }) => {
  const navigate = useNavigate();
  
  // Default header config
  const defaultHeader = {
    eyebrow: 'Renten-Check',
    headline: 'Wie groß ist deine Rentenlücke?',
    subline: 'Finde heraus, wie viel dir im Alter monatlich fehlen könnte.',
  };
  
  const header = headerConfig || defaultHeader;
  
  // Eingaben
  const [alter, setAlter] = useState(32);
  const [nettoEinkommen, setNettoEinkommen] = useState(4500);
  const [rentenniveau, setRentenniveau] = useState(80);
  const [gesetzlicheRente, setGesetzlicheRente] = useState(1500);
  const [privateVorsorge, setPrivateVorsorge] = useState(0);
  
  // Wachstumsraten (Annahmen - von Nico bestätigen lassen vor Livegang)
  const ZIELRENTE_WACHSTUM = 0.02; // 2% p.a. für einkommensabgeleitete Zielrente
  const VORSORGE_WACHSTUM = 0.015; // 1,5% p.a. für bestehende Altersvorsorge (GRV + privat)

  // Berechnungen nach NEUER FORMEL (Nico-Skizze)
  const results = useMemo(() => {
    // Jahre bis Rente (Rentenalter 67)
    const jahreBisRente = Math.max(0, 67 - alter);
    
    // --- HEUTE ---
    // Gewünschte Rente basierend auf Rentenniveau (heutige Kaufkraft)
    const gewuenschteRenteHeute = Math.round(nettoEinkommen * (rentenniveau / 100));
    // Bestehende Altersvorsorge heute
    const bestehendeVorsorgeHeute = gesetzlicheRente + privateVorsorge;
    
    // --- BEI RENTENBEGINN (NOMINAL) ---
    // Zielrente bei Rentenbeginn = Wunschrente heute × (1 + 2%)^Jahre
    const zielrenteFaktor = Math.pow(1 + ZIELRENTE_WACHSTUM, jahreBisRente);
    const zielrenteRentenbeginn = Math.round(gewuenschteRenteHeute * zielrenteFaktor);
    
    // Wachstumsfaktor für GRV (1,5% p.a.)
    const vorsorgeFaktor = Math.pow(1 + VORSORGE_WACHSTUM, jahreBisRente);
    
    // Separate Berechnung für GRV und Private bei Rentenbeginn (für Grafik)
    // GRV wird mit 1,5% p.a. inflationiert
    const gesetzlicheRenteRentenbeginn = Math.round(gesetzlicheRente * vorsorgeFaktor);
    // Private Vorsorge: 1:1 übernehmen (keine Inflation), da User bereits Auszahlungsbetrag eingibt
    const privateVorsorgeRentenbeginn = privateVorsorge;
    
    // Gedeckter Betrag bei Rentenbeginn = GRV (inflationiert) + Private (1:1)
    const gedeckterBetragRentenbeginn = gesetzlicheRenteRentenbeginn + privateVorsorgeRentenbeginn;
    
    // Rentenlücke bei Rentenbeginn = Zielrente - Gedeckter Betrag
    const renteLueckeRentenbeginn = Math.max(0, zielrenteRentenbeginn - gedeckterBetragRentenbeginn);
    
    // Rentenlücke HEUTE (heutige Kaufkraft) = Nominale Lücke ÷ Inflationsfaktor
    // Die nominale Lücke bei Rentenbeginn wird auf heutige Kaufkraft abgezinst
    const renteLueckeHeute = Math.round(renteLueckeRentenbeginn / zielrenteFaktor);
    
    // Deckungsgrad = Gedeckter Betrag / Zielrente bei Rentenbeginn
    const deckungProzent = zielrenteRentenbeginn > 0 
      ? Math.round((gedeckterBetragRentenbeginn / zielrenteRentenbeginn) * 100)
      : 0;
    
    // Lücke in Prozent = 100 - Deckungsgrad
    const lueckeProzent = Math.max(0, 100 - deckungProzent);
    
    // Stufe für Ergebnis-Anzeige (Farbcodierung)
    // Schwellenwerte von Nico bestätigen lassen vor Livegang:
    // Stufe 1: Rot/Kritisch (lueckeProzent > 50)
    // Stufe 2: Orange/Handlungsbedarf (lueckeProzent 25-50)
    // Stufe 3: Grün/Gut aufgestellt (lueckeProzent 0-25)
    const stufe = lueckeProzent > 50 ? 1 : lueckeProzent > 25 ? 2 : 3;
    
    return {
      // Heute
      gewuenschteRenteHeute,
      bestehendeVorsorgeHeute,
      renteLueckeHeute,
      // Bei Rentenbeginn
      zielrenteRentenbeginn,
      gedeckterBetragRentenbeginn,
      gesetzlicheRenteRentenbeginn,
      privateVorsorgeRentenbeginn,
      renteLueckeRentenbeginn,
      // Prozente & Meta
      lueckeProzent,
      deckungProzent,
      stufe,
      jahreBisRente,
      // Faktoren (für Anzeige)
      zielrenteFaktor,
      vorsorgeFaktor,
    };
  }, [alter, nettoEinkommen, rentenniveau, gesetzlicheRente, privateVorsorge, ZIELRENTE_WACHSTUM, VORSORGE_WACHSTUM]);

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-white relative" data-testid="renten-check">
      <div className="absolute inset-0 bg-gradient-to-b from-ice/50 via-transparent to-transparent" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 relative">
        {showHeader && (
          <div className="text-center mb-8 md:mb-12">
            <div className="text-[10px] sm:text-xs uppercase tracking-widest text-slate mb-3">
              {header.eyebrow}
            </div>
            <h2 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-navy tracking-tight mb-3 md:mb-4 px-2">
              {header.headline}
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-charcoal max-w-2xl mx-auto px-2">
              {header.subline}
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-[400px,1fr] gap-6 md:gap-8">
          {/* Linke Spalte - Wrapper muss sich strecken */}
          <div className="relative">
            <div className="lg:sticky lg:top-24 bg-bone rounded-2xl p-5 sm:p-6 md:p-8 border border-black/5 shadow-lg">
              <h3 className="font-outfit text-lg sm:text-xl font-bold text-navy mb-4 sm:mb-6">Deine Situation</h3>

            {/* Alter */}
            <div className="mb-5 sm:mb-6">
              <div className="flex justify-between items-baseline mb-2 sm:mb-3">
                <label className="text-xs sm:text-sm font-medium text-charcoal">Dein Alter</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={alter}
                    onChange={(e) => setAlter(Math.min(Math.max(parseInt(e.target.value) || 25, 25), 60))}
                    className="font-mono text-lg sm:text-xl font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-12"
                    min={25}
                    max={60}
                    data-testid="renten-input-alter"
                  />
                  <span className="font-mono text-lg sm:text-xl font-bold text-navy">Jahre</span>
                </div>
              </div>
              <Slider
                value={[alter]}
                onValueChange={(value) => setAlter(value[0])}
                min={25}
                max={60}
                step={1}
                className="w-full"
                data-testid="renten-slider-alter"
              />
              <div className="flex justify-between text-xs text-slate mt-2">
                <span>25 Jahre</span>
                <span className="text-cobalt font-medium">Noch {results.jahreBisRente} Jahre bis 67</span>
                <span>60 Jahre</span>
              </div>
            </div>

            {/* Nettoeinkommen */}
            <div className="mb-5 sm:mb-6">
              <div className="flex justify-between items-baseline mb-2 sm:mb-3">
                <label className="text-xs sm:text-sm font-medium text-charcoal">Aktuelles Nettoeinkommen</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={nettoEinkommen}
                    onChange={(e) => setNettoEinkommen(Math.min(Math.max(parseInt(e.target.value) || 2000, 2000), 15000))}
                    className="font-mono text-lg sm:text-xl font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-20 sm:w-24"
                    min={2000}
                    max={15000}
                    data-testid="renten-input-netto"
                  />
                  <span className="font-mono text-lg sm:text-xl font-bold text-navy whitespace-nowrap">€/Monat</span>
                </div>
              </div>
              <Slider
                value={[nettoEinkommen]}
                onValueChange={(value) => setNettoEinkommen(value[0])}
                min={2000}
                max={15000}
                step={100}
                className="w-full"
                data-testid="renten-slider-netto"
              />
              <div className="flex justify-between text-xs text-slate mt-2">
                <span>2.000 €</span>
                <span>15.000 €</span>
              </div>
            </div>

            {/* Gewünschtes Rentenniveau */}
            <div className="mb-5 sm:mb-6">
              <div className="flex justify-between items-baseline mb-2 sm:mb-3">
                <label className="text-xs sm:text-sm font-medium text-charcoal">Gewünschtes Rentenniveau</label>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={rentenniveau}
                    onChange={(e) => setRentenniveau(Math.min(Math.max(parseInt(e.target.value) || 50, 50), 100))}
                    className="font-mono text-lg sm:text-xl font-bold text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-12"
                    min={50}
                    max={100}
                    data-testid="renten-input-niveau"
                  />
                  <span className="font-mono text-lg sm:text-xl font-bold text-navy">%</span>
                </div>
              </div>
              <Slider
                value={[rentenniveau]}
                onValueChange={(value) => setRentenniveau(value[0])}
                min={50}
                max={100}
                step={5}
                className="w-full"
                data-testid="renten-slider-niveau"
              />
              <div className="flex justify-between text-xs text-slate mt-2">
                <span>50 %</span>
                <span className="text-cobalt font-medium">= {formatCurrency(results.gewuenschteRenteHeute)}/Monat</span>
                <span>100 %</span>
              </div>
            </div>

            {/* Bestehende Vorsorge */}
            <div className="pt-4 border-t border-black/5">
              <h4 className="text-xs sm:text-sm font-semibold text-navy mb-4">Bestehende Altersvorsorge</h4>
              
              {/* Gesetzliche Rente */}
              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-xs text-charcoal">Geschätzte gesetzliche Rente</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={gesetzlicheRente}
                      onChange={(e) => setGesetzlicheRente(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 4000))}
                      className="font-mono text-sm font-medium text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-16"
                      min={0}
                      max={4000}
                      data-testid="renten-input-gesetzlich"
                    />
                    <span className="text-xs text-slate whitespace-nowrap">€/Monat</span>
                  </div>
                </div>
                <Slider
                  value={[gesetzlicheRente]}
                  onValueChange={(value) => setGesetzlicheRente(value[0])}
                  min={0}
                  max={4000}
                  step={50}
                  className="w-full"
                  data-testid="renten-slider-gesetzlich"
                />
                <p className="text-[10px] text-slate mt-1">Findest du auf deiner Renteninformation</p>
              </div>

              {/* Private Altersvorsorge */}
              <div>
                <div className="flex justify-between items-baseline mb-2">
                  <label className="text-xs text-charcoal">Bestehende private Vorsorge (Auszahlung)</label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      value={privateVorsorge}
                      onChange={(e) => setPrivateVorsorge(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 5000))}
                      className="font-mono text-sm font-medium text-navy text-right bg-transparent border-b border-navy/20 focus:border-cobalt focus:outline-none w-16"
                      min={0}
                      max={5000}
                      data-testid="renten-input-privat"
                    />
                    <span className="text-xs text-slate whitespace-nowrap">€/Monat</span>
                  </div>
                </div>
                <Slider
                  value={[privateVorsorge]}
                  onValueChange={(value) => setPrivateVorsorge(value[0])}
                  min={0}
                  max={5000}
                  step={50}
                  className="w-full"
                  data-testid="renten-slider-privat"
                />
                <p className="text-[10px] text-slate mt-1">Riester, Betriebsrente, private Rentenversicherung, etc.</p>
              </div>
            </div>
            </div>
          </div>

          {/* Ergebnis - scrollbar (längere Spalte) */}
          <div className="space-y-4">
            {/* Grafische Darstellung - Timeline */}
            <motion.div 
              className="bg-white rounded-xl p-4 sm:p-6 md:p-8 border border-black/5 shadow-sm overflow-hidden"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* SVG Timeline Chart - VERGRÖSSERT */}
              <div className="relative w-full" style={{ height: '420px' }}>
                <svg viewBox="0 0 650 340" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                  {/* Hintergrund für Labels */}
                  <defs>
                    <filter id="labelBg" x="-0.1" y="-0.1" width="1.2" height="1.2">
                      <feFlood floodColor="white" floodOpacity="0.9"/>
                      <feComposite in="SourceGraphic"/>
                    </filter>
                  </defs>
                  
                  {/* X-Achse (Zeitlinie) */}
                  <line x1="60" y1="290" x2="580" y2="290" stroke="#cbd5e1" strokeWidth="2"/>
                  
                  {/* Alter-Labels auf Achse */}
                  <text x="95" y="315" fill="#1e3a5f" textAnchor="middle" fontSize="16" fontWeight="700">
                    Alter {alter}
                  </text>
                  <text x="500" y="315" fill="#1e3a5f" textAnchor="middle" fontSize="16" fontWeight="700">
                    Alter 67
                  </text>
                  
                  {/* === LINKE SEITE: HEUTE === */}
                  {/* Balken 1: Wunschrente heute (Netto) */}
                  <rect 
                    x="60" 
                    y={290 - Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220)} 
                    width="50" 
                    height={Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220)}
                    fill="#1e3a5f" 
                    rx="4"
                  />
                  <text 
                    x="85" 
                    y={290 - Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220) - 10} 
                    fill="#1e3a5f" 
                    textAnchor="middle" 
                    fontSize="14" 
                    fontWeight="700"
                    fontFamily="ui-monospace, monospace"
                  >
                    {formatCurrency(results.gewuenschteRenteHeute)}
                  </text>
                  
                  {/* Balken 2: Gesetzliche Rente heute (grün) */}
                  <rect 
                    x="120" 
                    y={290 - Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220)} 
                    width="50" 
                    height={Math.max(Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220), 3)}
                    fill="#10b981" 
                    rx="4"
                  />
                  {gesetzlicheRente > 0 && (
                    <text 
                      x="145" 
                      y={290 - Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220) - 10} 
                      fill="#10b981" 
                      textAnchor="middle" 
                      fontSize="14" 
                      fontWeight="700"
                      fontFamily="ui-monospace, monospace"
                    >
                      {formatCurrency(gesetzlicheRente)}
                    </text>
                  )}
                  
                  {/* === WACHSTUMSLINIEN === */}
                  {/* Linie 1: Wunschrente heute → Zielrente (durchgezogen) */}
                  <line 
                    x1="110" 
                    y1={290 - Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220)} 
                    x2="455" 
                    y2={290 - 220}
                    stroke="#1e3a5f" 
                    strokeWidth="2.5"
                    strokeOpacity="0.7"
                  />
                  {/* Label mit weißem Hintergrund */}
                  <rect 
                    x="245" 
                    y={290 - Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220) - 55}
                    width="90"
                    height="20"
                    fill="white"
                    rx="4"
                  />
                  <text 
                    x="290" 
                    y={290 - Math.min((results.gewuenschteRenteHeute / results.zielrenteRentenbeginn) * 220, 220) - 40} 
                    fill="#1e3a5f" 
                    textAnchor="middle" 
                    fontSize="13"
                    fontWeight="600"
                  >
                    ×1,02<tspan baselineShift="super" fontSize="9">{results.jahreBisRente}</tspan>
                  </text>
                  
                  {/* Linie 2: GRV heute → GRV Rentenbeginn (gestrichelt) */}
                  {gesetzlicheRente > 0 && (
                    <>
                      <line 
                        x1="170" 
                        y1={290 - Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220)} 
                        x2="455" 
                        y2={290 - Math.min((results.gesetzlicheRenteRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                        stroke="#10b981" 
                        strokeWidth="2.5"
                        strokeDasharray="8,5"
                        strokeOpacity="0.7"
                      />
                      {/* Label mit weißem Hintergrund */}
                      <rect 
                        x="260" 
                        y={290 - Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220) + 8}
                        width="100"
                        height="20"
                        fill="white"
                        rx="4"
                      />
                      <text 
                        x="310" 
                        y={290 - Math.min((gesetzlicheRente / results.zielrenteRentenbeginn) * 220, 220) + 22} 
                        fill="#10b981" 
                        textAnchor="middle" 
                        fontSize="13"
                        fontWeight="600"
                      >
                        ×1,015<tspan baselineShift="super" fontSize="9">{results.jahreBisRente}</tspan>
                      </text>
                    </>
                  )}
                  
                  {/* === RECHTE SEITE: BEI RENTENBEGINN === */}
                  {/* Gesamtrahmen für Zielrente */}
                  <rect 
                    x="455" 
                    y={290 - 220} 
                    width="60" 
                    height="220"
                    fill="white" 
                    stroke="#cbd5e1"
                    strokeWidth="1"
                    rx="4"
                  />
                  
                  {/* Gesetzliche Rente (unten, grün) */}
                  <rect 
                    x="455" 
                    y={290 - Math.min((results.gesetzlicheRenteRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)} 
                    width="60" 
                    height={Math.min((results.gesetzlicheRenteRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                    fill="#10b981" 
                    rx="4"
                  />
                  
                  {/* Private Vorsorge (darüber, orange) */}
                  {results.privateVorsorgeRentenbeginn > 0 && (
                    <rect 
                      x="455" 
                      y={290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)} 
                      width="60" 
                      height={Math.min((results.privateVorsorgeRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                      fill="#f59e0b" 
                      rx="4"
                    />
                  )}
                  
                  {/* Lücke (oben, nur roter Rahmen ohne Füllung) */}
                  {results.renteLueckeRentenbeginn > 0 && (
                    <rect 
                      x="455" 
                      y={290 - 220} 
                      width="60" 
                      height={Math.min((results.renteLueckeRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                      fill="none" 
                      stroke="#dc2626"
                      strokeWidth="3"
                      rx="4"
                    />
                  )}
                  
                  {/* Zielrente Label (oben) */}
                  <text 
                    x="485" 
                    y={290 - 220 - 12} 
                    fill="#1e3a5f" 
                    textAnchor="middle" 
                    fontSize="15" 
                    fontWeight="700"
                    fontFamily="ui-monospace, monospace"
                  >
                    {formatCurrency(results.zielrenteRentenbeginn)}
                  </text>
                  
                  {/* GRV Label (im grünen Bereich) */}
                  {results.gesetzlicheRenteRentenbeginn > 0 && (
                    <text 
                      x="485" 
                      y={290 - Math.min((results.gesetzlicheRenteRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) / 2} 
                      fill="white" 
                      textAnchor="middle" 
                      fontSize="12" 
                      fontWeight="700"
                      fontFamily="ui-monospace, monospace"
                    >
                      {formatCurrency(results.gesetzlicheRenteRentenbeginn)}
                    </text>
                  )}
                  
                  {/* Private Vorsorge Label (im orangen Bereich oder daneben wenn zu klein) */}
                  {results.privateVorsorgeRentenbeginn > 0 && (
                    <text 
                      x="485" 
                      y={
                        // Wenn orange Balken zu klein (<30px), Label links daneben
                        Math.min((results.privateVorsorgeRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) < 30
                          ? 290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) - 8
                          : 290 - Math.min((results.gesetzlicheRenteRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) - Math.min((results.privateVorsorgeRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) / 2
                      } 
                      fill={Math.min((results.privateVorsorgeRentenbeginn / results.zielrenteRentenbeginn) * 220, 220) < 30 ? "#f59e0b" : "white"}
                      textAnchor="middle" 
                      fontSize="12" 
                      fontWeight="700"
                      fontFamily="ui-monospace, monospace"
                    >
                      {formatCurrency(results.privateVorsorgeRentenbeginn)}
                    </text>
                  )}
                  
                  {/* === LÜCKE-MARKIERUNG (Klammer rechts) === */}
                  {results.renteLueckeRentenbeginn > 0 && (
                    <>
                      {/* Vertikale Linie der Klammer */}
                      <line 
                        x1="530" 
                        y1={290 - 220} 
                        x2="530" 
                        y2={290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                        stroke="#dc2626" 
                        strokeWidth="4"
                      />
                      {/* Oberer Haken */}
                      <line 
                        x1="522" 
                        y1={290 - 220} 
                        x2="538" 
                        y2={290 - 220}
                        stroke="#dc2626" 
                        strokeWidth="4"
                      />
                      {/* Unterer Haken */}
                      <line 
                        x1="522" 
                        y1={290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)} 
                        x2="538" 
                        y2={290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)}
                        stroke="#dc2626" 
                        strokeWidth="4"
                      />
                      {/* Lücke-Betrag */}
                      <text 
                        x="555" 
                        y={(290 - 220 + 290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)) / 2 - 5} 
                        fill="#dc2626" 
                        textAnchor="start" 
                        fontSize="16" 
                        fontWeight="700"
                        fontFamily="ui-monospace, monospace"
                      >
                        {formatCurrency(results.renteLueckeRentenbeginn)}
                      </text>
                      <text 
                        x="555" 
                        y={(290 - 220 + 290 - Math.min((results.gedeckterBetragRentenbeginn / results.zielrenteRentenbeginn) * 220, 220)) / 2 + 12} 
                        fill="#64748b" 
                        textAnchor="start" 
                        fontSize="12"
                        fontWeight="500"
                      >
                        Lücke
                      </text>
                    </>
                  )}
                  
                  {/* Legende (oben links) */}
                  <rect x="50" y="12" width="12" height="12" fill="#1e3a5f" rx="2"/>
                  <text x="67" y="22" fill="#475569" fontSize="11" fontWeight="500">Wunschrente</text>
                  
                  <rect x="160" y="12" width="12" height="12" fill="#10b981" rx="2"/>
                  <text x="177" y="22" fill="#475569" fontSize="11" fontWeight="500">Gesetzl. Rente</text>
                  
                  <rect x="280" y="12" width="12" height="12" fill="#f59e0b" rx="2"/>
                  <text x="297" y="22" fill="#475569" fontSize="11" fontWeight="500">Private Vorsorge</text>
                  
                  <rect x="410" y="12" width="12" height="12" fill="none" stroke="#dc2626" strokeWidth="2" rx="2"/>
                  <text x="427" y="22" fill="#475569" fontSize="11" fontWeight="500">Lücke</text>
                </svg>
              </div>
              
              {/* Annahmen-Hinweis */}
              <div className="mt-2 pt-3 border-t border-black/5">
                <p className="text-[11px] text-slate leading-relaxed">
                  <span className="font-medium">Annahmen:</span> Die Wunschrente wächst mit 2 % p. a. – das entspricht dem Inflationsziel der Europäischen Zentralbank, an das sich Einkommen langfristig etwa angleichen. Die gesetzliche Rente wird konservativ mit 1,5 % p. a. fortgeschrieben – niedriger als die vom Rentenversicherungsbericht 2025 der Bundesregierung prognostizierte durchschnittliche Rentensteigerung von 2,8 % p. a., als Sicherheitsabschlag für das ab 2031 sinkende Rentenniveau und steigende Steuer-/Abgabenlast auf Renten. Deine bestehende private Vorsorge wird 1:1 übernommen, da du bereits den erwarteten Auszahlungsbetrag eingibst.
                </p>
                <p className="text-[9px] text-slate/70 mt-2">
                  Quellen:{' '}
                  <a 
                    href="https://www.ecb.europa.eu/mopo/strategy/pricestab/html/index.de.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-slate"
                  >
                    Europäische Zentralbank (Preisstabilitätsziel)
                  </a>
                  {' · '}
                  <a 
                    href="https://www.bundesregierung.de/breg-de/aktuelles/rentenbericht-2025-2394260" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-slate"
                  >
                    Rentenversicherungsbericht 2025, Bundesregierung
                  </a>
                </p>
              </div>
            </motion.div>
            
            {/* Hauptergebnis: Zahlen-Box */}
            <motion.div 
              className={`rounded-xl p-4 sm:p-6 border-2 ${
                results.stufe === 1 
                  ? 'bg-red-50 border-red-300' 
                  : results.stufe === 2 
                    ? 'bg-amber-50 border-amber-300' 
                    : 'bg-emerald-50/50 border-emerald-200'
              }`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
            >
              {/* Lücke als Hauptzahl */}
              <div className="mb-3">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className={`font-mono text-3xl sm:text-4xl font-bold ${
                    results.stufe === 1 ? 'text-red-700' : 
                    results.stufe === 2 ? 'text-amber-700' : 'text-emerald-700'
                  }`}>
                    {formatCurrency(results.renteLueckeRentenbeginn)}
                  </span>
                  <span className={`text-sm ${
                    results.stufe === 1 ? 'text-red-600' : 
                    results.stufe === 2 ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    / Monat fehlen bei Rentenbeginn
                  </span>
                </div>
                {results.jahreBisRente > 0 && (
                  <p className={`text-xs mt-1 ${
                    results.stufe === 1 ? 'text-red-600' : 
                    results.stufe === 2 ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    Entspricht heute einer Kaufkraft von {formatCurrency(results.renteLueckeHeute)}/Monat.
                  </p>
                )}
              </div>
              
              {/* Stufen-spezifischer Text */}
              <p className={`text-sm sm:text-base leading-relaxed ${
                results.stufe === 1 ? 'text-red-800' : 
                results.stufe === 2 ? 'text-amber-800' : 'text-emerald-800'
              }`}>
                {results.stufe === 1 && (
                  <>Bei <span className="font-semibold">{formatCurrency(results.renteLueckeRentenbeginn)}</span> fehlen dir <span className="font-semibold">{results.lueckeProzent} %</span> deiner Zielrente. Das würde ich zeitnah angehen.</>
                )}
                {results.stufe === 2 && (
                  <>Es fehlen <span className="font-semibold">{formatCurrency(results.renteLueckeRentenbeginn)}</span> ({results.lueckeProzent} %) zur Zielrente. Das lässt sich meist gut aufbauen.</>
                )}
                {results.stufe === 3 && (
                  <>Deine Altersvorsorge deckt <span className="font-semibold">{results.deckungProzent} %</span> deiner Zielrente ab. Prüfe alle 2 bis 3 Jahre, ob sich etwas geändert hat.</>
                )}
              </p>
              
              {/* CTA je nach Stufe */}
              <div className="mt-4">
                {results.stufe === 1 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full group flex items-center justify-center gap-2 px-5 py-3.5 bg-red-600 text-white rounded-xl font-semibold text-sm sm:text-base hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/25"
                    data-testid="renten-cta-kritisch"
                  >
                    Altersvorsorge besprechen
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
                {results.stufe === 2 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full group flex items-center justify-center gap-2 px-5 py-3 bg-amber-600 text-white rounded-xl font-medium text-sm hover:bg-amber-700 transition-all duration-300 shadow-lg shadow-amber-600/25"
                    data-testid="renten-cta-optimierung"
                  >
                    Altersvorsorge besprechen
                    <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                )}
                {results.stufe === 3 && (
                  <button
                    onClick={() => navigate('/strategiegespraech')}
                    className="w-full text-center text-sm text-emerald-700 hover:text-emerald-800 transition-colors py-2"
                    data-testid="renten-cta-gut"
                  >
                    Trotzdem mal durchsprechen? →
                  </button>
                )}
              </div>
            </motion.div>

            {/* Detailübersicht */}
            <motion.div 
              className="bg-bone rounded-xl p-4 sm:p-6 border border-black/5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h4 className="text-sm font-semibold text-navy mb-4">Übersicht</h4>
              
              <div className="space-y-3">
                {/* Heute */}
                <div className="text-xs uppercase tracking-wider text-slate mb-2">Heute</div>
                <div className="flex items-center justify-between py-2 border-b border-black/5">
                  <span className="text-sm text-charcoal flex items-center">
                    Gewünschte Rente ({rentenniveau} %)
                    <InfoTooltip text="Deine gewünschte Rente heute, berechnet als Prozentsatz deines aktuellen Nettoeinkommens – ein gängiger Richtwert, um den Lebensstandard im Ruhestand zu halten. Prozentsatz individuell anpassbar." />
                  </span>
                  <span className="font-mono text-sm font-medium text-navy">
                    {formatCurrency(results.gewuenschteRenteHeute)}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-black/5">
                  <span className="text-sm text-charcoal flex items-center">
                    Bestehende Altersvorsorge
                    <InfoTooltip text="Deine gesetzliche Rente und private Vorsorge in heutigen Werten, wie in deiner Renteninformation bzw. deinen Standmitteilungen ausgewiesen." />
                  </span>
                  <span className="font-mono text-sm font-medium text-navy">
                    {formatCurrency(results.bestehendeVorsorgeHeute)}
                  </span>
                </div>
                
                {/* Bei Rentenbeginn */}
                <div className="text-xs uppercase tracking-wider text-slate mb-2 mt-4">Bei Rentenbeginn (in {results.jahreBisRente} Jahren)</div>
                <div className="flex items-center justify-between py-2 border-b border-black/5">
                  <span className="text-sm text-charcoal flex items-center">
                    Zielrente <span className="text-[10px] text-slate ml-1">(+2% p.a.)</span>
                    <InfoTooltip text="Deine Wunschrente, hochgerechnet auf den Rentenbeginn mit 2 % p. a. – das entspricht der langfristigen Inflationsannahme (EZB-Zielwert), an die sich Einkommen etwa angleichen." />
                  </span>
                  <span className="font-mono text-sm font-medium text-cobalt">
                    {formatCurrency(results.zielrenteRentenbeginn)}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-black/5">
                  <span className="text-sm text-charcoal flex items-center">
                    Gedeckter Betrag
                    <InfoTooltip text="Was von deiner bestehenden Altersvorsorge bei Rentenbeginn tatsächlich zur Verfügung steht – gesetzliche Rente und private Vorsorge, unterschiedlich hochgerechnet (siehe unten)." />
                  </span>
                  <span className="font-mono text-sm font-medium text-emerald">
                    {formatCurrency(results.gedeckterBetragRentenbeginn)}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-black/5">
                  <span className="text-sm text-charcoal pl-3 flex items-center">
                    – davon GRV <span className="text-[10px] text-slate ml-1">(+1,5% p.a.)</span>
                    <InfoTooltip text="Deine gesetzliche Rente, hochgerechnet mit einer konservativen Rentenanpassung von 1,5 % p. a. – niedriger als die vom Rentenversicherungsbericht 2025 der Bundesregierung prognostizierte durchschnittliche Steigerung von 2,8 % p. a., als Sicherheitsabschlag für sinkendes Rentenniveau und steigende Steuer-/Abgabenlast." />
                  </span>
                  <span className="font-mono text-sm font-medium text-emerald-600">
                    {formatCurrency(results.gesetzlicheRenteRentenbeginn)}
                  </span>
                </div>
                {results.privateVorsorgeRentenbeginn > 0 && (
                  <div className="flex items-center justify-between py-2 border-b border-black/5">
                    <span className="text-sm text-charcoal pl-3 flex items-center">
                      – davon Privat <span className="text-[10px] text-slate ml-1">(1:1)</span>
                      <InfoTooltip text="Deine private Vorsorge wird unverändert übernommen – Standmitteilungen zeigen meist bereits eine Hochrechnung auf den Rentenbeginn, eine zusätzliche Verzinsung würde diese doppelt zählen." />
                    </span>
                    <span className="font-mono text-sm font-medium text-amber-600">
                      {formatCurrency(results.privateVorsorgeRentenbeginn)}
                    </span>
                  </div>
                )}

                <div className={`flex flex-col py-3 -mx-4 sm:-mx-6 px-4 sm:px-6 rounded-lg ${
                  results.stufe === 1 ? 'bg-red-100' :
                  results.stufe === 2 ? 'bg-amber-100' : 'bg-emerald-100'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium flex items-center ${
                      results.stufe === 1 ? 'text-red-800' :
                      results.stufe === 2 ? 'text-amber-800' : 'text-emerald-800'
                    }`}>
                      Rentenlücke
                      <InfoTooltip text="Differenz zwischen Zielrente und gedecktem Betrag bei Rentenbeginn – die Lücke, die zusätzlich geschlossen werden sollte." />
                    </span>
                    <span className={`font-mono text-lg font-bold ${
                      results.stufe === 1 ? 'text-red-700' :
                      results.stufe === 2 ? 'text-amber-700' : 'text-emerald-700'
                    }`}>
                      {formatCurrency(results.renteLueckeRentenbeginn)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className={`text-xs flex items-center ${
                      results.stufe === 1 ? 'text-red-600' :
                      results.stufe === 2 ? 'text-amber-600' : 'text-emerald-600'
                    }`}>
                      Deckungsgrad
                      <InfoTooltip text="Wie viel Prozent deiner Zielrente durch deine bestehende Altersvorsorge bereits gedeckt sind." />
                    </span>
                    <span className={`font-mono text-xs font-medium ${
                      results.stufe === 1 ? 'text-red-600' :
                      results.stufe === 2 ? 'text-amber-600' : 'text-emerald-600'
                    }`}>
                      {results.deckungProzent} %
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hinweis Bundesregierung + Beratungsnutzen */}
        <div className="mt-10 md:mt-16 max-w-3xl mx-auto space-y-6">
          {/* Bundesregierung Hinweis */}
          <div className="bg-bone rounded-xl p-5 sm:p-6 border border-black/5">
            <p className="text-sm text-charcoal leading-relaxed">
              Auch die Bundesregierung stuft die gesetzliche Rente nur als Fundament ein, das durch private Vorsorge ergänzt werden sollte.{' '}
              <a 
                href="https://www.bundesfinanzministerium.de/Content/DE/FAQ/reform-der-privaten-altersvorsorge.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cobalt hover:text-navy font-medium underline underline-offset-2"
              >
                Mehr dazu beim Bundesfinanzministerium →
              </a>
            </p>
          </div>

          {/* Warum Beratung trotz ETF-Sparplan */}
          <div className="bg-white rounded-xl p-5 sm:p-6 border border-black/5 shadow-sm">
            <h4 className="font-outfit text-base sm:text-lg font-bold text-navy mb-4">
              Deine Lücke lässt sich rechnerisch auch mit einem simplen ETF-Sparplan schließen. Eine Beratung lohnt sich trotzdem:
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-cobalt mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-charcoal leading-relaxed">
                    <span className="font-semibold text-navy">Steuerlicher Schutzmantel:</span> In einer fondsgebundenen Rentenversicherung wächst dein Kapital innerhalb des Vertrags steuerlich begünstigt, ohne die laufende Abgeltungssteuer, die im freien Depot bei Umschichtungen anfällt.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-cobalt mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-charcoal leading-relaxed">
                    <span className="font-semibold text-navy">Steuervorteile beim Sparen selbst:</span> Bei der Basisrente (Rürup) sind Beiträge in der Ansparphase steuerlich absetzbar. Das kann deine effektive Sparleistung erhöhen, ohne dass du netto mehr aufwendest.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-cobalt mt-2 flex-shrink-0" />
                <div>
                  <p className="text-sm text-charcoal leading-relaxed">
                    <span className="font-semibold text-navy">Individuelle Optimierung:</span> Wie stark sich das für dich lohnt, hängt von deiner Steuersituation ab. Im Gespräch rechne ich dir konkret vor, was ein ETF-Sparplan und was eine Police für dich tatsächlich bedeuten würde.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 sm:mt-8 flex items-start gap-2 text-[10px] sm:text-xs text-slate max-w-3xl mx-auto px-2">
          <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
          <p>
            Diese Berechnung dient als erste Orientierung und erfolgt in heutigen Euro-Beträgen (nominal) vor Steuern. 
            Die gesetzliche Rente unterliegt der nachgelagerten Besteuerung, private Renten der Ertragsanteilbesteuerung. 
            Durch Inflation kann die Kaufkraft der genannten Beträge bis zur Rente deutlich sinken. 
            Die tatsächliche Nettolücke kann daher höher oder niedriger ausfallen. Eine persönliche Beratung ist empfehlenswert.
          </p>
        </div>
      </div>
    </section>
  );
};

export default RentenCheck;
